import { type ReactNode } from 'react';
import { Scripts, ScrollRestoration, useLoaderData, useLocation } from 'react-router';
import {
  computeDataTemplate,
  GoogleTagManagerBody,
  shouldRenderGtm,
} from '@cx-web/components/third-party/google-tag-manager';
import { featureFlags, useFeatureFlag } from '@cx-web/feature-flags/provider';
import { ExternalScripts } from 'remix-utils/external-scripts';

import { PageProvider } from '~/providers/page-context';

import { type loader } from './loader.server';
import PageTemplateImpl from './route-impl';

export default function Body({ children }: { children?: ReactNode }) {
  const rootLoaderData = useLoaderData<typeof loader>();
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { pathname } = useLocation();
  const disableGtm = useFeatureFlag(featureFlags.DISABLE_GOOGLE_TAG_MANAGER);
  const disableOffersWidgets = useFeatureFlag(featureFlags.DISABLE_OFFER_WIDGETS);

  const { ENV, currentPathWithRepub, deviceType, gtmId, url } = rootLoaderData;

  return (
    <body data-fpsm-theme="oddschecker" data-template={computeDataTemplate(pathname)}>
      <GoogleTagManagerBody
        gtmId={gtmId}
        shouldRender={!disableGtm && shouldRenderGtm(ENV.ENVIRONMENT, { allowLocalhost: ENV.ENABLE_LOCALHOST_GTM })}
      />
      <PageProvider
        ENV={ENV}
        currentPathWithRepub={currentPathWithRepub}
        deviceType={deviceType}
        fullPageUrl={url}
        gtmId={gtmId}
      >
        <PageTemplateImpl />
      </PageProvider>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}`,
        }}
      />
      <ScrollRestoration />
      {!disableOffersWidgets && <ExternalScripts />}
      <Scripts />
      {children}
    </body>
  );
}
